import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Utility function to detect iOS devices
 * Used for App Store compliance - no tracking on iOS
 */
export const isIOSDevice = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isIOSWebView = isIOS && !!window?.webkit?.messageHandlers;
  const isStandalonePWA = window.navigator.standalone === true;
  return isIOS || isIOSWebView || isStandalonePWA;
};

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // NEVER show cookie banner for iOS users (App Store compliance)
    // We do not track iOS users - only essential cookies for app functionality
    if (isIOSDevice()) {
      // Set flag that iOS user has necessary-only cookies (no tracking)
      const iosConsent = {
        necessary: true,
        analytics: false,
        marketing: false,
        timestamp: new Date().toISOString(),
        platform: 'ios',
        tracking_enabled: false
      };
      localStorage.setItem('cookieConsent', JSON.stringify(iosConsent));
      // Ensure banner never shows
      setShowBanner(false);
      return;
    }

    // For non-iOS: Check if consent has already been given
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay to prevent banner flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allConsent));
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const customConsent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(customConsent));
    setShowBanner(false);
    setShowSettings(false);
  };

  // NEVER render cookie banner on iOS (App Store compliance - no tracking)
  if (isIOSDevice()) return null;
  
  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6" data-testid="cookie-consent-banner">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden">
        {!showSettings ? (
          // Main Banner View
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-full flex-shrink-0">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-2">
                  We Value Your Privacy
                </h3>
                <p className="text-[#57534E] text-sm leading-relaxed mb-4">
                  We use cookies to enhance your browsing experience, remember your preferences, 
                  and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. 
                  You can manage your preferences or learn more in our{' '}
                  <Link to="/privacy-policy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="bg-primary text-white hover:bg-primary/90 rounded-full px-6"
                    data-testid="cookie-accept-all"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Accept All
                  </Button>
                  <Button
                    onClick={handleAcceptNecessary}
                    variant="outline"
                    className="rounded-full px-6"
                    data-testid="cookie-accept-necessary"
                  >
                    Necessary Only
                  </Button>
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="ghost"
                    className="text-[#57534E] hover:text-[#2D2D2D] rounded-full px-6"
                    data-testid="cookie-manage-settings"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Preferences
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Settings View
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-nunito font-bold text-[#2D2D2D]">
                Cookie Preferences
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-stone-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-[#57534E]" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between p-4 bg-stone-50 rounded-lg">
                <div className="flex-1 pr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[#2D2D2D]">Necessary Cookies</span>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      Required
                    </span>
                  </div>
                  <p className="text-sm text-[#57534E]">
                    Essential for the website to function properly. These cannot be disabled.
                  </p>
                </div>
                <div className="flex items-center">
                  <div className="w-12 h-6 bg-primary rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between p-4 bg-stone-50 rounded-lg">
                <div className="flex-1 pr-4">
                  <span className="font-semibold text-[#2D2D2D] block mb-1">Analytics Cookies</span>
                  <p className="text-sm text-[#57534E]">
                    Help us understand how visitors interact with our website to improve user experience.
                  </p>
                </div>
                <button
                  onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.analytics ? 'bg-primary justify-end' : 'bg-stone-300 justify-start'
                  }`}
                  data-testid="cookie-toggle-analytics"
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </button>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between p-4 bg-stone-50 rounded-lg">
                <div className="flex-1 pr-4">
                  <span className="font-semibold text-[#2D2D2D] block mb-1">Marketing Cookies</span>
                  <p className="text-sm text-[#57534E]">
                    Used to track visitors across websites to display relevant advertisements.
                  </p>
                </div>
                <button
                  onClick={() => setPreferences(prev => ({ ...prev, marketing: !prev.marketing }))}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.marketing ? 'bg-primary justify-end' : 'bg-stone-300 justify-start'
                  }`}
                  data-testid="cookie-toggle-marketing"
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleSavePreferences}
                className="bg-primary text-white hover:bg-primary/90 rounded-full px-6"
                data-testid="cookie-save-preferences"
              >
                Save Preferences
              </Button>
              <Button
                onClick={handleAcceptAll}
                variant="outline"
                className="rounded-full px-6"
              >
                Accept All
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
